import { useState } from "react";
import { useProducts } from "@/hooks/useProducts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { INSTALLATION_PHASES } from "@/utils/installationPhases";
import { Edit, GripVertical, EyeOff, Eye } from "lucide-react";
import { ProductDrawer } from "./price-book/ProductDrawer";
import type { Database } from "@/integrations/supabase/types";

type Product = Database["public"]["Tables"]["products"]["Row"];

export function PhaseProductsManager() {
  const { products, isLoading, updateProduct } = useProducts();
  const [selectedPhase, setSelectedPhase] = useState<string | null>(
    INSTALLATION_PHASES[0]?.key || null
  );
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Group products by phase
  const productsByPhase = products?.reduce((acc, product) => {
    const phase = product.install_phase || 'unassigned';
    if (!acc[phase]) acc[phase] = [];
    acc[phase].push(product);
    return acc;
  }, {} as Record<string, Product[]>) || {};

  // Sort products within each phase by display_order
  Object.keys(productsByPhase).forEach(phase => {
    productsByPhase[phase].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  });

  const currentProducts = selectedPhase 
    ? productsByPhase[selectedPhase] || []
    : [];

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setDrawerOpen(true);
  };

  const handleToggleActive = async (product: Product) => {
    await updateProduct.mutateAsync({
      id: product.id,
      is_active: !product.is_active,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        {INSTALLATION_PHASES.map((phase) => (
          <Button
            key={phase.key}
            variant={selectedPhase === phase.key ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedPhase(phase.key)}
          >
            Phase {phase.number}: {phase.title}
            {productsByPhase[phase.key] && (
              <Badge variant="secondary" className="ml-2">
                {productsByPhase[phase.key].length}
              </Badge>
            )}
          </Button>
        ))}
        <Button
          variant={selectedPhase === 'unassigned' ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedPhase('unassigned')}
        >
          Unassigned
          {productsByPhase['unassigned'] && (
            <Badge variant="secondary" className="ml-2">
              {productsByPhase['unassigned'].length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {currentProducts.map((product) => (
          <Card key={product.id} className="p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-start gap-3">
              <GripVertical className="h-5 w-5 text-muted-foreground mt-1 cursor-move" />
              <div className="flex-1 space-y-3">
                {product.image_url && (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-32 object-cover rounded"
                  />
                )}
                <div>
                  <h4 className="font-semibold text-sm line-clamp-2">{product.name}</h4>
                  {product.brand && (
                    <p className="text-xs text-muted-foreground">{product.brand}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-xs">
                    {product.category}
                  </Badge>
                  {!product.is_active && (
                    <Badge variant="destructive" className="text-xs">
                      Inactive
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(product)}
                    className="flex-1"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleToggleActive(product)}
                  >
                    {product.is_active ? (
                      <EyeOff className="h-3 w-3" />
                    ) : (
                      <Eye className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {currentProducts.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>No products assigned to this phase yet.</p>
          <p className="text-sm mt-2">
            Edit products in the Price Book to assign them to installation phases.
          </p>
        </div>
      )}

      <ProductDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingProduct(null);
        }}
        product={editingProduct}
      />
    </div>
  );
}
